/*
 * Naturlabs cart page (sections/nl-cart-main.liquid).
 * <nl-cart-page data-cart-section="{section id}">: the element itself is kept, its children are re-rendered by
 * NL.cart (Section Rendering API) after every cart change, so all listeners are delegated on the host.
 *   - quantity: [data-line-quantity][data-line-key] change (debounced) -> NL.cart.change(key, qty)
 *   - remove:   [data-cart-remove][data-line-key] click (a link to url_to_remove without JS) -> row collapses, qty 0
 *   - coupon:   form[data-coupon-form] -> /discount/CODE?redirect=<cart> (discounts are applied in checkout)
 *   - note:     textarea[data-cart-note] -> NL.cart.update({}, { note }) debounced
 *   - sticky:   [data-cart-sticky] bar on phones while the summary CTA is below the viewport
 *   - busy:     the row is dimmed with a spinner on the pressed control, checkout waits until the cart is saved
 *   - errors:   cart API messages (422 stock limits) are shown inline under the row after a refresh
 *   - focus:    after the re-render focus returns to the same control (or the next row)
 *   - live:     polite announcement from the server rendered [data-cart-page-message] (prices without ,00)
 * Depends on assets/nl-app.js (window.NL). Respects prefers-reduced-motion.
 */
(() => {
  'use strict';

  if (customElements.get('nl-cart-page')) return;

  const reduced = () =>
    window.NL && window.NL.motion
      ? window.NL.motion.reduced
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ALERT_ICON =
    '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>';

  const kindOf = (control) => {
    if (!control || !control.matches) return 'input';
    if (control.matches('[data-cart-remove]')) return 'remove';
    if (control.matches('[data-qty-step="1"]')) return 'plus';
    if (control.matches('[data-qty-step="-1"]')) return 'minus';
    return 'input';
  };

  const controlOf = (row, kind) => {
    if (!row) return null;
    if (kind === 'remove') return row.querySelector('[data-cart-remove]');
    if (kind === 'plus') return row.querySelector('[data-qty-step="1"]');
    if (kind === 'minus') return row.querySelector('[data-qty-step="-1"]');
    return row.querySelector('[data-line-quantity]');
  };

  class NLCartPage extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.pending = new Map();
      this.live = this.parentElement && this.parentElement.querySelector('[data-cart-live]');

      this.busy = 0;
      this.pressed = { key: null, kind: null };
      this.addEventListener(
        'click',
        (event) => {
          const control = event.target.closest('li[data-line-key] :is([data-qty-step], [data-cart-remove])');
          if (!control) return;
          const row = control.closest('li[data-line-key]');
          this.pressed = { key: row ? row.dataset.lineKey : null, kind: kindOf(control) };
        },
        true
      );
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      // Checkout waits until the cart is saved (summary and sticky buttons submit the cart form with name=checkout)
      this.onCheckoutGuard = (event) => {
        const submitter = event.submitter;
        if (this.busy > 0 && submitter && submitter.name === 'checkout') event.preventDefault();
      };
      document.addEventListener('submit', this.onCheckoutGuard, true);
      this.addEventListener('input', (event) => this.onInput(event));

      this.onCartUpdated = (event) => {
        const detail = event.detail || {};
        this.announce(detail.cart);
        this.setupSticky();
      };
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.setupSticky();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      document.removeEventListener('submit', this.onCheckoutGuard, true);
      if (this.stickyObserver) this.stickyObserver.disconnect();
      this._bound = false;
    }

    get cart() {
      return window.NL && window.NL.cart;
    }

    /* ---- Remove ---- */
    onClick(event) {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove || !this.cart) return;
      event.preventDefault();
      const row = remove.closest('.nl-cart-line') || remove.closest('li[data-line-key]');
      this.collapse(row);
      this.pressed = { key: remove.dataset.lineKey, kind: 'remove' };
      this.updateLine(remove.dataset.lineKey, 0, row);
    }

    collapse(row) {
      if (!row || reduced() || !row.animate) return;
      const height = row.getBoundingClientRect().height;
      row.style.overflow = 'hidden';
      row.animate(
        [
          { opacity: 1, height: `${height}px` },
          { opacity: 0, height: '0px', paddingTop: '0px', paddingBottom: '0px' },
        ],
        { duration: 360, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' }
      );
    }

    /* ---- Quantity ---- */
    onChange(event) {
      const input = event.target.closest('[data-line-quantity]');
      if (!input || !this.cart) return;
      const key = input.dataset.lineKey;
      window.clearTimeout(this.pending.get(key));
      this.pending.set(
        key,
        window.setTimeout(() => {
          const quantity = Math.max(0, parseInt(input.value, 10) || 0);
          const row = input.closest('.nl-cart-line') || input.closest('li[data-line-key]');
          if (quantity === 0) this.collapse(row);
          this.updateLine(key, quantity, row);
        }, 320)
      );
    }

    async updateLine(key, quantity, row) {
      const kind = this.pressed.key === key && this.pressed.kind ? this.pressed.kind : 'input';
      this.pressed = { key: null, kind: null };
      const rows = Array.from(this.querySelectorAll('li[data-line-key]'));
      const index = Math.max(0, rows.indexOf(row));
      const hadFocus = this.contains(document.activeElement);
      this.clearErrors();
      this.busy += 1;
      this.setBusy(row, kind, true);
      try {
        await this.cart.change(key, quantity, { source: 'cart-page' });
        this.restoreFocus(key, kind, index, hadFocus);
      } catch (error) {
        const strings = (window.NL && window.NL.strings) || {};
        const message = (error && error.message) || strings.errorGeneric || '';
        try {
          await this.cart.refresh('cart-page-error');
        } catch (refreshError) {
          /* offline: keep the markup */
        }
        this.showError(key, message);
        this.restoreFocus(key, kind, index, hadFocus);
      } finally {
        this.busy = Math.max(0, this.busy - 1);
        if (row && row.isConnected) this.setBusy(row, kind, false);
        if (this.busy === 0) {
          this.querySelectorAll('[data-cart-checkout]').forEach((button) => button.removeAttribute('aria-disabled'));
        }
      }
    }

    setBusy(row, kind, busy) {
      if (busy) this.querySelectorAll('[data-cart-checkout]').forEach((button) => button.setAttribute('aria-disabled', 'true'));
      if (!row) return;
      if (busy) row.setAttribute('aria-busy', 'true');
      else row.removeAttribute('aria-busy');
      const control = controlOf(row, kind);
      if (control && kind !== 'input') control.classList.toggle('is-pressed', busy);
    }

    rowByKey(key) {
      return key ? this.querySelector(`li[data-line-key="${CSS.escape(String(key))}"]`) : null;
    }

    restoreFocus(key, kind, index, hadFocus) {
      // Also restore when focus fell to the body (a stepper button that turns disabled at 0 drops focus)
      const active = document.activeElement;
      const lost = !active || active === document.body;
      if (!hadFocus && !lost) return;
      const row = this.rowByKey(key);
      let target = null;
      if (row) {
        target = controlOf(row, kind);
        if (!target || target.disabled) target = controlOf(row, 'input');
      } else {
        const rows = Array.from(this.querySelectorAll('li[data-line-key]'));
        const next = rows[Math.min(index, rows.length - 1)];
        target = next ? next.querySelector('.nl-cart-row__title') : null;
        if (!target) {
          const heading = this.closest('section') && this.closest('section').querySelector('h1');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            target = heading;
          }
        }
      }
      if (target) target.focus();
    }

    clearErrors() {
      this.querySelectorAll('.nl-cart-page__error').forEach((el) => el.remove());
    }

    showError(key, message) {
      if (!message) return;
      this.clearErrors();
      const row = this.rowByKey(key);
      const slot = row && row.querySelector('[data-line-error-slot]');
      if (!slot) {
        if (window.NL && window.NL.toast) window.NL.toast(message, { variant: 'error' });
        return;
      }
      const box = document.createElement('p');
      box.className = 'nl-cart-page__error';
      box.setAttribute('role', 'alert');
      box.innerHTML = ALERT_ICON;
      const text = document.createElement('span');
      text.textContent = message;
      box.append(text);
      slot.append(box);
    }

    /* ---- Coupon ---- */
    onSubmit(event) {
      const form = event.target.closest('[data-coupon-form]');
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector('input[name="discount"]');
      const error = form.querySelector('[data-coupon-error]');
      const code = input ? input.value.trim() : '';
      if (!code) {
        if (input) {
          input.setAttribute('aria-invalid', 'true');
          input.focus();
        }
        if (error) {
          error.textContent = this.dataset.requiredMessage || '';
          error.hidden = false;
        }
        return;
      }
      if (input) input.removeAttribute('aria-invalid');
      if (error) error.hidden = true;
      const button = form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');
      const redirect = this.dataset.cartUrl || '/cart';
      window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
    }

    /* ---- Note ---- */
    onInput(event) {
      const field = event.target.closest('[data-cart-note]');
      if (field) {
        window.clearTimeout(this.noteTimer);
        this.noteTimer = window.setTimeout(() => {
          if (this.cart) this.cart.update({}, { note: field.value, source: 'cart-note' }).catch(() => {});
        }, 600);
        return;
      }
      const coupon = event.target.closest('[data-coupon-form] input');
      if (coupon && coupon.getAttribute('aria-invalid') === 'true' && coupon.value.trim()) {
        coupon.removeAttribute('aria-invalid');
        const error = coupon.form && coupon.form.querySelector('[data-coupon-error]');
        if (error) error.hidden = true;
      }
    }

    /* ---- Live region with the new total ---- */
    announce(cart) {
      if (!this.live || !cart) return;
      const source = this.querySelector('[data-cart-page-message]');
      const message = source ? source.textContent.replace(/\s+/g, ' ').trim() : '';
      window.clearTimeout(this.announceTimer);
      this.live.textContent = '';
      this.announceTimer = window.setTimeout(() => {
        this.live.textContent = message;
      }, 120);
    }

    /* ---- Sticky checkout bar (phones and tablets) ---- */
    setupSticky() {
      if (this.stickyObserver) this.stickyObserver.disconnect();
      const bar = this.querySelector('[data-cart-sticky]');
      const target = this.querySelector('[data-cart-checkout]');
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const below = !entry.isIntersecting && entry.boundingClientRect.top > 0;
          bar.classList.toggle('is-visible', below);
        },
        { threshold: 0 }
      );
      this.stickyObserver.observe(target);
    }
  }

  customElements.define('nl-cart-page', NLCartPage);
})();
